import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Award, Calendar, Heart, Shield, Target, User, Users, Zap } from 'lucide-react';
import API from '../../api/API';
import { BASE } from './links';
import ClassificaCard from '../../components/statistiche/ClassificaCard';
import { LinkGiocatore, LinkSquadra } from '../../components/statistiche/Link';
import './Statistiche.css';
import './Hub.css';

/**
 * L'ingresso della sezione: i tre indici, e sotto le classifiche di sempre.
 *
 * Sono le stesse sei classifiche personali che una edizione mostra da sempre — miglior
 * realizzatore, scalpatore, regista, per maschi e femmine — ma calcolate su tutti i tornei
 * invece che su uno solo, più le stesse tre statistiche viste per squadra. Arrivano già
 * pronte da api/carriera/classifiche: calcolarle qui vorrebbe dire scaricare l'archivio.
 */

/** Le sei personali, nell'ordine in cui le mostra anche la pagina di una edizione. */
const PERSONALI = [
  { campo: 'meteMaschi', titolo: 'Miglior Realizzatore', icona: Award, tono: 'realizzatore' },
  { campo: 'meteFemmine', titolo: 'Miglior Realizzatrice', icona: Award, tono: 'realizzatrice' },
  { campo: 'pieniMaschi', titolo: 'Miglior Scalpatore', icona: Target, tono: 'scalpatore' },
  { campo: 'pieniFemmine', titolo: 'Miglior Scalpatrice', icona: Target, tono: 'scalpatrice' },
  { campo: 'assistMaschi', titolo: 'Miglior Regista M', icona: Zap, tono: 'regista-m' },
  { campo: 'assistFemmine', titolo: 'Miglior Regista F', icona: Heart, tono: 'regista-f' },
];

/** Per una squadra il sesso non ha senso: restano le tre statistiche. */
const SQUADRE = [
  { campo: 'squadreMete', titolo: 'Più mete', icona: Award },
  { campo: 'squadrePieni', titolo: 'Più pieni', icona: Target },
  { campo: 'squadreAssist', titolo: 'Più assist', icona: Zap },
];

const vociGiocatori = (elenco) => (elenco ?? []).map((g) => ({
  chiave: g.id,
  nome: <LinkGiocatore id={g.id} nome={g.nome} cognome={g.cognome} />,
  contesto: g.gruppo,
  valore: g.valore,
}));

const vociSquadre = (elenco) => (elenco ?? []).map((s) => ({
  chiave: s.slug,
  nome: <LinkSquadra slug={s.slug} nome={s.nome} />,
  valore: s.valore,
}));

export default function Hub() {
  const [conti, setConti] = useState({ edizioni: null, squadre: null, giocatori: null });
  const [classifiche, setClassifiche] = useState(null);

  useEffect(() => {
    let vivo = true;
    // allSettled e non all: se cade un conteggio devono restare gli altri due e le classifiche,
    // invece di azzerarsi tutti insieme perché una sola chiamata è andata storta
    Promise.allSettled([
      API.statistiche.tornei(),
      API.carriera.squadre(),
      API.carriera.giocatori({ page: 1, size: 1 }),
      API.carriera.classifiche(),
    ]).then(([tornei, squadre, giocatori, cl]) => {
      if (!vivo) return;
      const ok = (e) => e.status === 'fulfilled' && e.value?.isSuccess;
      setConti({
        edizioni: ok(tornei) ? tornei.value.data.reduce((n, t) => n + (t.edizioni?.length || 0), 0) : null,
        squadre: ok(squadre) ? squadre.value.data.length : null,
        giocatori: ok(giocatori) ? giocatori.value.data.totale : null,
      });
      if (ok(cl)) setClassifiche(cl.value.data);
    });
    return () => { vivo = false; };
  }, []);

  const conto = (n, singolare, plurale) => (n === null ? '' : `${n} ${n === 1 ? singolare : plurale}`);

  return (
    <>
      <nav className="hub-indici">
        <Link className="hub-riquadro" to={`${BASE}/edizioni`}>
          <Calendar className="hub-icona" aria-hidden="true" />
          <span className="hub-riquadro-nome">Edizioni</span>
          <span className="hub-riquadro-conto">{conto(conti.edizioni, 'edizione', 'edizioni')}</span>
        </Link>
        <Link className="hub-riquadro" to={`${BASE}/squadre`}>
          <Users className="hub-icona" aria-hidden="true" />
          <span className="hub-riquadro-nome">Squadre</span>
          <span className="hub-riquadro-conto">{conto(conti.squadre, 'squadra', 'squadre')}</span>
        </Link>
        <Link className="hub-riquadro" to={`${BASE}/giocatori`}>
          <User className="hub-icona" aria-hidden="true" />
          <span className="hub-riquadro-nome">Giocatori</span>
          <span className="hub-riquadro-conto">{conto(conti.giocatori, 'giocatore', 'giocatori')}</span>
        </Link>
      </nav>

      {classifiche && (
        <>
          <section className="hub-sezione">
            <h2 className="hub-sezione-titolo">
              <Award className="hub-sezione-icona" aria-hidden="true" />
              Classifiche personali di sempre
            </h2>
            <div className="classifiche-griglia">
              {PERSONALI.map((c) => (
                <ClassificaCard
                  key={c.campo}
                  titolo={c.titolo}
                  icona={c.icona}
                  tono={c.tono}
                  voci={vociGiocatori(classifiche[c.campo])}
                />
              ))}
            </div>
          </section>

          <section className="hub-sezione">
            <h2 className="hub-sezione-titolo">
              <Shield className="hub-sezione-icona" aria-hidden="true" />
              Classifiche di squadra di sempre
            </h2>
            <div className="classifiche-griglia a-tre">
              {SQUADRE.map((c) => (
                <ClassificaCard
                  key={c.campo}
                  titolo={c.titolo}
                  icona={c.icona}
                  tono="squadra"
                  voci={vociSquadre(classifiche[c.campo])}
                />
              ))}
            </div>
          </section>
        </>
      )}
    </>
  );
}
